import { Component } from '@angular/core';
import { CognitoService } from './auth/services/cognito.service';
import { getMessaging, getToken, onMessage } from '@angular/fire/messaging';
import { environment } from 'src/environments/environment';
import { FirebaseApp } from '@angular/fire/app';
import { ActionsService } from './shared/services/actions.service';
import { TiposMensaje } from './shared/enums/tipos-mensaje.enum';
import Swal, { SweetAlertIcon } from 'sweetalert2';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MaintenanceService } from './shared/services/maintenance.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { NetworkCheckService } from './shared/services/network-check.service';
import { SessionExpiryService } from './shared/services/session-expiry.service';
import { SessionResumeService } from './shared/services/session-resume.service';
import { loginReturnParams, readReturnUrl } from './shared/utils/session-tabs';

// Constantes para verificación de registro pendiente
import {
  PASOS_EMPRESA,
  PASOS_PERSONA,
} from './auth/components/dinamic-signup/constants/signup.constants';

const SIGNUP_STATE_KEY = 'aguacate_signup_state';
const SIGNUP_STATE_EXPIRY_MS = 30 * 60 * 1000; // 30 minutos

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass'],
})
export class AppComponent {
  title = 'aguacate-wallet';
  isCheckingAuth = true;
  private networkMonitoringStarted = false;

  token = '';
  constructor(
    private afApp: FirebaseApp,
    private cognitoService: CognitoService,
    private router: Router,
    private actionsService: ActionsService,
    private maintenanceService: MaintenanceService,
    private spinner: NgxSpinnerService,
    private networkCheckService: NetworkCheckService,
    private sessionExpiry: SessionExpiryService,
    private sessionResume: SessionResumeService,
  ) {}

  message: any = null;

  listen() {
    const messaging = getMessaging(this.afApp);
    onMessage(messaging, (payload: any) => {
      this.manageNotification(payload.data.action);
    });
  }

  requestPermission() {
    const messaging = getMessaging(this.afApp);
    getToken(messaging, { vapidKey: environment.firebase.applicationServerKey })
      .then((currentToken) => {
        if (currentToken) {
          this.token = currentToken;
        } else {
        }
      })
      .catch((err) => {
      });
  }

  showMessage(title: string, iconName: SweetAlertIcon = 'success') {
    const Toast = Swal.mixin({
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 6000,
      timerProgressBar: true,
    });

    Toast.fire({
      icon: iconName,
      title: title,
    });
  }

  manageNotification(action: TiposMensaje) {
    if (action === TiposMensaje.RECARGAR_SALDO) {
      this.actionsService.emitirAccionRecargarSaldo();
      this.showMessage('Saldo abonado a tu cuenta');
    }
    if (action === TiposMensaje.ACTUALIZAR_TRANSACCIONES_ASIGNADAS) {
      this.actionsService.emitirRecargarTransaccionesAsignadas();
      this.showMessage('Transacciones seleccionadas por otro operador', 'info');
    }
    if (action === TiposMensaje.ACTUALIZAR_TASA_FIAT) {
      this.actionsService.emitirAccionTasaFiat();
    }
    if (action === TiposMensaje.ACTUALIZAR_USDT) {
      this.actionsService.emitirAccionTasaUSDT();
    }
  }

  parseQueryParams(queryString: string | undefined): { [key: string]: string } {
    if (!queryString) {
      return {};
    }

    return queryString
      .split('&')
      .reduce((params: { [key: string]: string }, param: string) => {
        const [key, value] = param.split('=');
        params[key] = value;
        return params;
      }, {});
  }

  public async ngOnInit(): Promise<void> {
    // El registro de pagador es público y de marca blanca: no hay sesión que
    // revisar, y cualquier redirección (mantenimiento, login) mostraría Aguacate.
    if (
      window.location.pathname.startsWith('/pagador-registro') ||
      window.location.pathname.startsWith('/registro/')
    ) {
      this.isCheckingAuth = false;
      return;
    }

    // Tope de 12 h desde el login. Se arranca antes que nada para que una
    // pestaña que quedó abierta sin actividad igual se corte a horario.
    this.sessionExpiry.start();
    // Al volver a entrar en una pestaña, las demás del operador que quedaron
    // en el login regresan a su página.
    this.sessionResume.start();

    // Firebase messaging solo en rutas autenticadas (post-login)
    // Evita pedir permisos de notificaciones/PWA antes del login
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/wallet') || currentPath.startsWith('/retail')) {
      try {
        this.requestPermission();
        this.listen();
      } catch (e) {
        console.warn('[AppComponent] Firebase messaging no disponible:', e);
      }
    }
    this.checkMantenimientoRealtime();

    // Registrar listener de navegación ANTES de los awaits
    // para no perder eventos NavigationEnd durante checkInitialAuth()
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe(async (event: NavigationEnd) => {
        const currentRoute = event.urlAfterRedirects;

        const isAuthRoute = currentRoute.startsWith('/auth');
        const isRoot = currentRoute === '/' || currentRoute === '';


        // CRÍTICO: Si el spinner todavía está activo, ocultarlo ahora
        // Esto maneja el caso donde checkInitialAuth() redirigió y retornó true
        if (this.isCheckingAuth && !isRoot) {
          this.spinner.hide('initial-auth-check');
          this.isCheckingAuth = false;
        }

        // Si checkInitialAuth aún está corriendo, no interferir
        // (evita race condition con checkPendingRegistration)
        if (this.isCheckingAuth) {
          return;
        }

        const isAuthenticated = await this.cognitoService.isAuthenticated();

        // Monitoreo de calidad de conexión solo para operadores
        if (isAuthenticated && currentRoute.startsWith('/wallet') && !this.networkMonitoringStarted) {
          try {
            const user = await this.cognitoService.getUser();
            const role = user?.attributes?.['custom:user-group'];
            if (role === 'aguacate_operator') {
              this.networkMonitoringStarted = true;
              this.networkCheckService.startMonitoring();
            }
          } catch (_) {}
        }

        // Si el usuario está autenticado y navega manualmente a root,
        // redirigir a su home correspondiente
        if (isAuthenticated && isRoot) {
          const user = await this.cognitoService.getUser();
          const role = user?.attributes?.['custom:user-group'];

          if (role === 'aguacate_retail_client' || role === 'aguacate_retail_cashier') {
            this.router.navigate(['/retail/home']);
          } else if (role === 'aguacate_client' || role === 'aguacate_operator') {
            this.router.navigate(['/wallet']);
          }
          return;
        }

        // Si el usuario está autenticado y manualmente navega a /auth/login,
        // redirigirlo a su home correspondiente
        if (isAuthenticated && isAuthRoute) {
          // Esperar un poco para dar tiempo al componente de login de hacer su redirección
          setTimeout(async () => {
            // Verificar si todavía está en la ruta de auth
            if (this.router.url.startsWith('/auth')) {
              const user = await this.cognitoService.getUser();
              const role = user?.attributes?.['custom:user-group'];

              if (role === 'aguacate_retail_client' || role === 'aguacate_retail_cashier') {
                this.router.navigate(['/retail/home']);
              } else if (role === 'aguacate_operator') {
                this.router.navigateByUrl(readReturnUrl() ?? '/wallet');
              } else if (role === 'aguacate_client') {
                this.router.navigate(['/wallet']);
              }
            }
          }, 100);
        }
      });

    // FASE 0: Verificar si hay registro de persona pendiente
    const hasPendingRegistration = await this.checkPendingRegistration();
    if (hasPendingRegistration) {
      this.spinner.hide('initial-auth-check');
      this.isCheckingAuth = false;
      return;
    }

    // Verificar autenticación inicial
    const shouldKeepSpinner = await this.checkInitialAuth();

    if (!shouldKeepSpinner) {
      this.spinner.hide('initial-auth-check');
      this.isCheckingAuth = false;
    }

  }

  private async checkInitialAuth(): Promise<boolean> {
    try {
      const currentRoute = window.location.pathname;
      const isAuthRoute = currentRoute.startsWith('/auth');
      const isRoot = currentRoute === '/' || currentRoute === '';


      // Si es ruta de autenticación, no hacer nada
      if (isAuthRoute) {
        return false;
      }

      // Ejecutar en paralelo para no sumar latencias
      const [isAuthenticated, mantenimiento] = await Promise.all([
        this.cognitoService.isAuthenticated(),
        this.maintenanceService.isMaintenanceActive().catch(() => false),
      ]);

      if (mantenimiento) {
        this.cognitoService.signOut();
        if (isRoot) {
          window.location.href = 'https://landing.aguacatewallet.com';
          return true;
        }
        this.router.navigate(['/maintenance-global']);
        return false;
      }

      // Si NO está autenticado
      if (!isAuthenticated) {
        if (isRoot) {
          window.location.href = 'https://landing.aguacatewallet.com';
          return true;
        }
        // Si intenta acceder a rutas protegidas sin autenticación
        if (currentRoute.startsWith('/wallet') || currentRoute.startsWith('/retail')) {
          this.router.navigate(['/auth/login'], {
            queryParams: loginReturnParams(currentRoute + window.location.search),
          });
          return false;
        }
        return false;
      }

      // Si está autenticado y está en root, redirigir al home correspondiente
      if (isAuthenticated && isRoot) {
        const user = await this.cognitoService.getUser();
        const role = user?.attributes?.['custom:user-group'];

        if (role === 'aguacate_retail_client' || role === 'aguacate_retail_cashier') {
          this.router.navigate(['/retail/home']);
        } else if (role === 'aguacate_client' || role === 'aguacate_operator') {
          this.router.navigate(['/wallet']);
        } else {
          // Rol desconocido
          await this.cognitoService.signOut();
          this.router.navigate(['/auth/login']);
        }
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  /**
   * Polling de mantenimiento cada 30 segundos
   * Usa forceRefresh() para actualizar el caché y beneficiar a otros consumidores
   * Detecta cambios de estado de mantenimiento y redirige automáticamente
   */
  private checkMantenimientoRealtime(): void {
    setInterval(async () => {
      const isAuthenticated = await this.cognitoService.isAuthenticated();
      if (!isAuthenticated) return;

      // Usar forceRefresh() para ignorar caché y obtener valor fresco
      // Esto actualiza el caché para todas las demás llamadas a isMaintenanceActive()
      const mantenimiento = await this.maintenanceService.isMaintenanceActive();

      if (mantenimiento) {
        this.cognitoService.signOut();
        this.router.navigate(['/maintenance-global']);
      }
    }, 120000); // 2 minutos
  }

  /**
   * FASE 0: Verifica si hay un registro pendiente (persona o empresa)
   * Solo muestra alert si email y WhatsApp fueron verificados
   * @returns true si hubo redirección (continuar o abandonar), false si no hay registro pendiente
   */
  private async checkPendingRegistration(): Promise<boolean> {
    try {
      const stored = localStorage.getItem(SIGNUP_STATE_KEY);
      if (!stored) return false;

      const persisted = JSON.parse(stored);
      const now = Date.now();

      // Verificar si expiró (30 minutos)
      if (now - persisted.timestamp >= SIGNUP_STATE_EXPIRY_MS) {
        await this.clearPendingRegistration();
        return false;
      }

      const state = persisted.state;

      // Verificar que esté en un paso mayor a 1 y tenga tipo de cuenta
      if (!state?.currentStep || state.currentStep <= 1 || !state.accountType) {
        return false;
      }

      // Solo mostrar si ambas verificaciones están completas
      if (!state.emailVerified || !state.phoneVerified) {
        await this.clearPendingRegistration();
        return false;
      }

      // Si ya estamos en la ruta de dinamic-signup, no mostrar el alert
      const currentRoute = window.location.pathname;
      if (currentRoute.includes('/auth/dinamic-signup')) {
        return false;
      }


      // El resumen tiene que decir el mismo paso al que va a caer el usuario,
      // que lo decide `sanitizeRestoredState`: si las imagenes de identidad no
      // llegaron a S3 hay que volver a capturarlas.
      //
      // La regla es por tipo de cuenta: el paso de captura es el 5 en persona y
      // el 7 en empresa. Antes se aplicaba la de persona a las dos, y como
      // empresa no tenia `s3Keys` en ningun momento, a una empresa que iba por
      // el paso 12 el dialogo le decia "Paso 5".
      const pasos = state.accountType === 'empresas' ? PASOS_EMPRESA : PASOS_PERSONA;
      if (state.currentStep >= pasos.OCR && !state.s3Keys) {
        state.currentStep = pasos.OCR;
      }

      const isPersonal = state.accountType === 'personal';
      const accountLabel = isPersonal ? 'Personal' : 'Empresa';
      const accountIcon = isPersonal ? 'assets/img/users.png' : 'assets/img/wallet.png';
      const email = state.formData?.email || '';
      const phone = state.formData ? `${state.formData.phoneCode} ${state.formData.phone}` : '';

      // Construir resumen de pasos
      const stepsHtml = this.buildPendingRegistrationSummary(state, accountLabel, accountIcon, email, phone);

      const result = await Swal.fire({
        html: stepsHtml,
        showCancelButton: true,
        showCloseButton: false,
        confirmButtonText: 'Continuar registro',
        cancelButtonText: 'Iniciar sesi\u00f3n con otra cuenta',
        allowOutsideClick: false,
        allowEscapeKey: false,
        customClass: {
          popup: 'aguacate-swal-popup',
          confirmButton: 'aguacate-swal-confirm',
          cancelButton: 'aguacate-swal-cancel',
          htmlContainer: 'aguacate-swal-html',
          actions: 'aguacate-swal-actions',
        },
        buttonsStyling: false,
      });

      if (result.isConfirmed) {
        // Continuar registro - marcar flag para evitar doble alert en dinamic-signup
        sessionStorage.setItem('aguacate_continue_registration', 'true');
        this.router.navigate(['/auth/dinamic-signup']);
        return true;
      } else {
        // Abandonar e ir a login
        await this.clearPendingRegistration();
        this.router.navigate(['/auth/login']);
        return true;
      }

    } catch (err) {
      await this.clearPendingRegistration();
      return false;
    }
  }

  private buildPendingRegistrationSummary(
    state: any, accountLabel: string, accountIcon: string, email: string, phone: string
  ): string {
    const checkSvg = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#0e7705"/><path d="M4.5 8L7 10.5L11.5 5.5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const pendingSvg = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="#8a8f98"/><circle cx="8" cy="8" r="3" fill="white"/></svg>';

    const steps: string[] = [];

    // Correo
    if (email) {
      steps.push(`
        <div class="ag-step-row">
          <span class="ag-step-icon">${checkSvg}</span>
          <div class="ag-step-info"><span class="ag-step-label">CORREO</span><span class="ag-step-value">${email}</span></div>
          <span class="ag-step-badge done">Verificado</span>
        </div>`);
    }

    // WhatsApp
    if (phone) {
      steps.push(`
        <div class="ag-step-row">
          <span class="ag-step-icon">${checkSvg}</span>
          <div class="ag-step-info"><span class="ag-step-label">WHATSAPP</span><span class="ag-step-value">${phone}</span></div>
          <span class="ag-step-badge done">Verificado</span>
        </div>`);
    }

    // Pasos adicionales de persona
    if (state.accountType === 'personal') {
      if (state.ocrData) {
        steps.push(`
          <div class="ag-step-row">
            <span class="ag-step-icon">${checkSvg}</span>
            <div class="ag-step-info"><span class="ag-step-label">DOCUMENTO</span><span class="ag-step-value">Escaneado</span></div>
            <span class="ag-step-badge done">Verificado</span>
          </div>`);
      }
      if (state.faceVerified) {
        steps.push(`
          <div class="ag-step-row">
            <span class="ag-step-icon">${checkSvg}</span>
            <div class="ag-step-info"><span class="ag-step-label">VERIFICACI\u00d3N FACIAL</span><span class="ag-step-value">Completada</span></div>
            <span class="ag-step-badge done">Verificado</span>
          </div>`);
      }
    }

    // Total steps (empresa=4, persona=8 o 9)
    const totalSteps = state.accountType === 'empresas' ? 4
      : (state.politicamenteExpuesto === true ? 9 : 8);
    const progress = Math.round(((state.currentStep - 1) / totalSteps) * 100);

    return `
      <style>
        .aguacate-swal-popup { font-family: 'Poppins', sans-serif !important; border-radius: 16px !important; padding: 2rem 1.5rem !important; max-width: 400px !important; }
        .aguacate-swal-html { margin: 0 !important; padding: 0 !important; }
        .aguacate-swal-actions { flex-direction: column !important; gap: 0.5rem !important; width: 100% !important; padding: 0 !important; margin-top: 0 !important; }
        .aguacate-swal-confirm { font-family: 'Poppins', sans-serif !important; background-color: #0e7705 !important; color: white !important; border: none !important; border-radius: 8px !important; padding: 0.75rem 1.5rem !important; font-weight: 600 !important; font-size: 0.95rem !important; width: 100% !important; cursor: pointer !important; transition: background-color 0.2s !important; }
        .aguacate-swal-confirm:hover { background-color: #154e3a !important; }
        .aguacate-swal-cancel { font-family: 'Poppins', sans-serif !important; background-color: transparent !important; color: #8a8f98 !important; border: 1px solid #e5e7eb !important; border-radius: 8px !important; padding: 0.75rem 1.5rem !important; font-weight: 500 !important; font-size: 0.95rem !important; width: 100% !important; cursor: pointer !important; transition: all 0.2s !important; }
        .aguacate-swal-cancel:hover { background-color: #f9fafb !important; color: #082c22 !important; border-color: #8a8f98 !important; }
        .ag-header { text-align: center; margin-bottom: 1.25rem; }
        .ag-header img { width: 48px; height: 48px; margin-bottom: 0.5rem; }
        .ag-header-title { font-size: 1.1rem; font-weight: 700; color: #082c22; margin: 0; }
        .ag-header-type { display: inline-block; background-color: #b2f0c0; color: #154e3a; font-size: 0.75rem; font-weight: 600; padding: 0.2rem 0.75rem; border-radius: 999px; margin-top: 0.35rem; }
        .ag-progress-section { margin-bottom: 1rem; }
        .ag-progress-bar-bg { width: 100%; height: 8px; background-color: #e5e7eb; border-radius: 999px; overflow: hidden; }
        .ag-progress-bar-fill { height: 100%; background-color: #0e7705; border-radius: 999px; }
        .ag-progress-text { font-size: 0.75rem; color: #8a8f98; margin-top: 0.35rem; text-align: right; }
        .ag-steps-card { background-color: #f9fafb; border: 1px solid #e5e7eb; border-radius: 12px; padding: 0.75rem; margin-bottom: 1rem; }
        .ag-step-row { display: flex; align-items: center; padding: 0.5rem 0.25rem; border-bottom: 1px solid #f3f4f6; gap: 0.5rem; }
        .ag-step-row:last-child { border-bottom: none; }
        .ag-step-icon { flex-shrink: 0; display: flex; align-items: center; }
        .ag-step-info { flex: 1; display: flex; flex-direction: column; min-width: 0; }
        .ag-step-label { font-size: 0.7rem; color: #8a8f98; text-transform: uppercase; letter-spacing: 0.5px; }
        .ag-step-value { font-size: 0.85rem; color: #082c22; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .ag-step-badge { flex-shrink: 0; font-size: 0.65rem; font-weight: 600; padding: 0.15rem 0.5rem; border-radius: 999px; }
        .ag-step-badge.done { background-color: #b2f0c0; color: #154e3a; }
        .ag-step-badge.pending { background-color: #fef3c7; color: #92400e; }
      </style>
      <div class="ag-header">
        <img src="${accountIcon}" alt="${accountLabel}" />
        <p class="ag-header-title">Registro en progreso</p>
        <span class="ag-header-type">Cuenta ${accountLabel}</span>
      </div>
      <div class="ag-progress-section">
        <div class="ag-progress-bar-bg"><div class="ag-progress-bar-fill" style="width: ${progress}%"></div></div>
        <div class="ag-progress-text">Paso ${state.currentStep} de ${totalSteps}</div>
      </div>
      <div class="ag-steps-card">${steps.join('')}</div>
    `;
  }

  /**
   * Limpia todo el estado de registro pendiente
   * Incluye localStorage, sessionStorage y signOut de Cognito
   */
  private async clearPendingRegistration(): Promise<void> {
    try {
      // Limpiar storage
      localStorage.removeItem(SIGNUP_STATE_KEY);
      localStorage.removeItem('aguacate_registration_in_progress');
      sessionStorage.removeItem(SIGNUP_STATE_KEY);
      sessionStorage.removeItem('aguacate_registration_in_progress');
      sessionStorage.removeItem('aguacate_continue_registration');

      // SignOut de Cognito si hay sesión activa
      try {
        await this.cognitoService.signOut();
      } catch (err) {
      }
    } catch (err) {
    }
  }
}
